<template>
<div >
    <Head>
        <title>Dealer Tools</title>
    </Head>
    <!-- HEADER -->
    <div id="dealer-tools-header" data-allow-mismatch class="banner banner--dealer-tools banner--desktop-top-center">
        <div  v-if="builderData.banner" class="banner__background">
            <Media
                class="banner__image banner__image--desktop" 
                role="presentation"
                :media="builderData.banner"
                defaultMediaType="image"
            />
            <Media
                role="presentation"
                class="banner__image banner__image--mobile" 
                :media="builderData.banner"
                defaultMediaType="image"
            />
        </div>
        <div v-else class="banner__background">
            <Media
               
                class="banner__image banner__image--desktop" 
                role="presentation"
                :media="desktopHeroImage"
                defaultMediaType="image"
            />
            <Media
                role="presentation"
                class="banner__image banner__image--mobile" 
                :media="mobileHeroImage"
                defaultMediaType="image"
            />
        </div>
        <div class="banner__content gradient">
            <div class="banner__layout center-align bg-rothcoCharcoal">
                <h2 class="banner__headline headline-xl text-rothcoWhite" role="heading" aria-level="1">Dealer Tools</h2>
                <p class="banner__description paragraph-default text-rothcoWhite" role="heading" aria-level="4">
                    {{ builderData.headerMessage || 'Resources and tools to help you sell more effectively, streamline your inventory management, optimize your sales flow.' }}
                </p>
                <form class="input-folder--input input-folder input-search" @reset="searchTerm = ''">
                    <input type="text" id="tools-search" placeholder=" " v-model="searchTerm" @keypress.enter.prevent>
                    <label class="mobile-hidden" for="tools-search"><p class="paragraph-default">Search for a document, asset, or resource</p></label>
                    <label class="desktop-hidden" for="tools-search"><p class="paragraph-default">Search for a document...</p></label>
                    <button type="reset"></button>
                </form>
            </div>
            <div v-if="showLandingPage" class="banner__buttons">
                <h3 class="banner__headline headline-xs text-rothcoWhite" role="heading" aria-level="2">Quick Links</h3>
                <RothcoCta external :href="props?.catalog_page_url" target="_blank" rel="noopener noreferrer" class="primary dark" :button_icon="null" aria-label="Catalog Builder">
                    Catalog Builder
                </RothcoCta>
                <RothcoCta class="primary dark" :button_icon="null" aria-label="Hi-Res Product Images" @click="setFilterValue(imageAndAssetDirectory)">
                    Hi-Res Product Images
                </RothcoCta>
                <RothcoCta v-if="productVideosUrl" external :href="productVideosUrl" target="_blank" rel="noopener noreferrer" class="primary dark" :button_icon="null" aria-label="Product Assets">
                    Product Assets
                </RothcoCta>
                <RothcoCta class="primary dark" :button_icon="null" aria-label="API Documentation" :href="route('api.docs',['about-the-api'])">
                    API Documentation
                </RothcoCta>
                <RothcoCta v-if="pricingGroup" class="primary dark" :button_icon="null" :aria-label="`${currentYear} Price Lists`" @click="setFilterValue(pricingGroup?.directory_path)">
                    {{ currentYear }} Price Lists
                </RothcoCta>
            </div>
        </div>
    </div>
    <!-- SEARCH RESULTS -->
    <div v-if="!showLandingPage" :class="['dealer-tools-grid bg-rothcoCream', (showFilters ? 'drawer-open' : '')]">
        <div class="filter-bar">
            <span class="filter-bar-count">{{ pagination.totalItems }} Results</span>
            <div class="filter-sort-buttons">
                <button class="filter-button goback-button" aria-label="Go Back" @click="goBack()">
                    <span>Go Back</span>
                </button>
                <button @click="toggleFilters" class="filter-button" aria-label="Show/Hide filters">
                    <span>{{ showFilters ? "Hide" : "Show" }} Filters</span>
                    <span class="applied-filters-count"></span>
                </button>
            </div>
        </div>
        <div class="container-filter-product-grid">
            <ProductFilterDrawer
                ref="filterDrawer"
                v-model:showDrawer="showFilters"
                v-model:activeFilters="activeFilters"
                :facets="facets"
                :filtersKey="`filters-${paginationKey}-${filterDrawerKey}`"
                :numResults="toNumber(pagination.totalItems)"
                :sortOptions="[]"
                content-page
            />
            <div class="product-grid-container">
                <div class="product-grid resource-grid">
                    <DealerResource v-for="(resource, i) in results" :key="i" :resource="resource"/>
                </div>
                <pagination
                    v-if="paginationKey > 0"
                    class="pagination"
                    :pagination="pagination"
                    @pageChange="handlePagination"
                    :key="paginationKey"
                />
            </div>
        </div>
    </div>
    <!-- TOOLS LIST -->
    <section v-else class="grid_content-wrapper">
        <div v-for="(group, name) of toolsList" :key="name" class="grid_content">
            <h3 data-id="folder" class="grid_content-title title-l" role="heading" aria-level="3">{{ name }}</h3>

            <div class="grid_content-container grid_content-3-column">
                <div v-for="(subGroup, i) in group" :key="i" class="grid_content-column">
                    <a @click.prevent="setFilterValue(subGroup?.directory_path)" class="launcher-item launcher-long" role="button">
                        <div class="animation-desk">
                            <div class="launcher-item__wrapper">
                                <div class="fill">
                                    <div class="launcher-item__wrapper-text">
                                        <p class="title">{{ subGroup.name }} </p>
                                    </div>
                                    <div class="carret"> 
                                        <CarretsBlackIcon alt="" role="presentation"/>
                                    </div>
                                </div>
                            </div>
                            <div class="launcher-item__content">
                                <p class="paragraph-details">{{ subGroup.description }}</p>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </section>
    <Cms
        ref="cms"
        model="content-section"
        content-prop="content"
        :limit="1"
        driver="builder"
        :components="getBuilderComponents()"
        :readableFields="['banner','headerMessage','productVideosUrl']"
        v-model="builderData"
        :searchParams="searchParams"
        is-section
    />
</div>
</template>
<script setup lang="ts">
    import { ref, computed, watch, inject, nextTick } from "vue";
    import { watchDebounced } from "@vueuse/core";
    // import { registeredComponents } from "../../dealer-tools-page-init-builder.ts";
    import { getBuilderComponents } from "@root/builder-components.ts";
    import { useContent } from "@/helpers/composables/searchSpringApi";
    import { Link, router, Head } from "@inertiajs/vue3";
    import ProductFilterDrawer from "@/components/ProductFilterDrawer.vue";
    import DealerResource from "@/components/DealerResource.vue"
    import Pagination from "@/components/Pagination.vue"
    import RothcoCta from "@/components/RothcoCta.vue"
    import Media from "@/components/Media.vue";
    import Cms from "@/components/Cms.vue";
    import desktopHeroImage from "@images/batch4/desktop-hero.jpg";
    import mobileHeroImage from "@images/batch4/mobile-hero.jpg";
    import CarretsBlackIcon from "@icons/carrets-black.svg";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    import { useCasts } from "@/helpers/composables/casts";
    const props = defineProps({
        content: {
            type: Object,
            default: ()=>({})
        },
        groups: {
            type: Array,
            default: ()=>[]
        },
        catalog_page_url: {
            type: String,
            default: 'https://apps.rothco.com'
        }
    })

    let route = inject("route");
    let builderData = ref({
        banner: null,
        headerMessage: null,
        productVideosUrl: null
    });

    const productVideosUrl = computed(() => {
        const url = builderData.value.productVideosUrl?.trim();
        return url || null;
    });
    
    const searchTerm = ref('');
    const filterDrawer = ref(null);

    const toolsList = computed(()=>{
        return props?.groups?.reduce((acc, group) => {
            let key = group?.directory_path?.split('/')?.filter(str=>(!!str))?.[1];
            if(key){
                key = key.split('-').map((word)=>(word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
                acc[key] = acc?.[key] ? [...acc[key], group] : [group]
            }
            return acc;
        },{}) ?? {}
    })
    const imageAndAssetDirectory = ref('dealer-tools/dealer-assets');
    const pricingGroup = computed(()=>{
        return props?.groups.find((group)=>(group.name == 'Pricing'));
    })
    
    const { 
        searchSpringResults,
        handleFilter,
        handlePagination,
        paginationKey
    } = useContent({
        filters: [],
        sorts: [],
        searchTerm,
        bgFilter: {
            filter: 'bgfilter.ss_category_hierarchy',
            value: 'dealer-tools'
        }
    });

    const results = computed(()=>{
        return searchSpringResults.value?.results?.map(result=>{
            if(typeof DOMParser === 'undefined') {
                    return {
                    ...result,
                    folderName: []
                };
            }
            let categoryHierarchy = result.ss_category_hierarchy.sort((a, b) => b.length - a.length)?.[0];
            let parser = new DOMParser();
            let doc = parser.parseFromString(categoryHierarchy, 'text/html');

            let folderName = doc.documentElement.textContent?.split('>').slice(-1)?.[0];
            folderName = folderName?.split("-").map((word)=>(word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())).join(" ");
            return {
                ...result,
                folderName
            }
        }) ?? [];
    })
    const facets = computed(()=>{
        return searchSpringResults.value?.facets ?? [];
    })
    const pagination = computed(()=>{
        return searchSpringResults.value?.pagination ? {
            totalItems: searchSpringResults.value.pagination.totalResults,
            currentPage: searchSpringResults.value.pagination.currentPage,
            pageSize: searchSpringResults.value.pagination.perPage,
            maxPages: searchSpringResults.value.pagination.totalPages
        } : {totalItems: 0};
    })

    const showFilters = ref(false);
    function toggleFilters() {
        showFilters.value = !showFilters.value;
    }

    const activeFilters = ref([]);

    const setFilterValue = (path)=>{
        if(path){
            //need to refactor for category hierarchy filter
            let filterValue = path?.replaceAll('/'," ").trim().split(" ").join(">"); 
            activeFilters.value = [{
                facet: facets.value.find((facet)=>(facet.field == 'ss_category_hierarchy')),
                value: {value: filterValue, label: filterValue?.split('>').slice(-1)?.[0], active: true}
            }];
            nextTick(()=>{
                let breakpoints = useRothcoBreakpoints();
                showFilters.value = !breakpoints.smallerOrEqual('laptop').value;
                showLandingPage.value = false;
                document.getElementById('dealer-tools-header')?.scrollIntoView({behavior: "smooth"});
            })
        }
    }

    const showLandingPage = ref(true);
    watch([searchTerm, activeFilters], ([newSearchTerm, newActiveFilters])=>{
        // there is a 4 character min before searchspring returns results for content data feeds
        if(newSearchTerm.length > 3 || newActiveFilters?.length > 0){
            showLandingPage.value = false;
        }else if(newSearchTerm.length <= 3 && newActiveFilters?.length == 0){
            showLandingPage.value = true;
        }
    },{deep: true})
    
    const filterDrawerKey = ref(0);
    const goBack = ()=>{
        showLandingPage.value = true;
        searchTerm.value = "";
        showFilters.value = false;
        activeFilters.value = [];
        filterDrawerKey.value++;
    }

    watchDebounced(() => [...activeFilters.value], handleFilter, {
        debounce: 200
    });


    let currentYear = computed(()=>{
        return new Date().getFullYear();
    })

    let { toNumber } = useCasts();

    let searchParams = computed(()=>{
        return {
            'data.pageUrl.$eq': 'dealer-tools',
        }
    })
</script>
